/// <amd-module name="VSSUI/Components/Dialog/CustomDialog" />
import * as React from "react";
import { EventManagement } from "VSS/Core/EventManagement";
import { announce } from "VSS/Core/Util/Accessibility";
import { ScreenContext, ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { Callout, ContentJustification, ContentLocation, ContentOrientation, ContentSize } from "VSSUI/Callout";
import { ICustomDialogProps } from "VSSUI/Components/Dialog/Dialog.Props";
import { FocusZoneKeyStroke } from "VSSUI/FocusZone";
import { Icon } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import * as Resources from "VSSUI/Resources.Layer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Spacing, Surface, SurfaceContext } from "VSSUI/Surface";
import { css, KeyCode } from "VSSUI/Util";

interface ICustomDialogState {
    width?: number;
    height?: number;
}

const MIN_DIALOG_Size = 320;
const DIALOG_RESIZE_INCREMENT = 2;

export class CustomDialog extends React.Component<ICustomDialogProps, ICustomDialogState> {
    public static defaultProps = {
        contentJustification: ContentJustification.Center,
        contentLocation: ContentLocation.Center,
        escDismiss: true,
        lightDismiss: true,
        enterPrimary: true
    };

    public constructor(props: ICustomDialogProps) {
        super(props);
        this.state = {};
    }

    public static contextType = ScreenContext;

    private events = new EventManagement();
    private contentRef = React.createRef<HTMLDivElement>();
    private anchorX: number;
    private anchorY: number;
    private currentHeight: number;
    private currentWidth: number;

    public render(): JSX.Element {
        const {
            ariaDescribedBy,
            ariaLabel,
            ariaLabelledBy,
            blurDismiss,
            calloutClassName,
            className,
            calloutContentClassName,
            contentJustification,
            contentLocation,
            defaultActiveElement,
            defaultFocusableElementAriaLabel,
            escDismiss,
            id,
            lightDismiss,
            onDismiss,
            overlay,
            modal,
            resizable,
            role,
            contentSize = ContentSize.Medium
        } = this.props;

        return (
            <Observer size={this.context.size}>
                {(props: { size: ScreenSize }) => {
                    const mobile = props.size === ScreenSize.xsmall;

                    return (
                        <Callout
                            ariaDescribedBy={ariaDescribedBy}
                            ariaLabel={ariaLabel}
                            ariaLabelledBy={ariaLabelledBy}
                            blurDismiss={blurDismiss}
                            className={css(calloutClassName, "bolt-dialog-callout")}
                            contentClassName={css(
                                calloutContentClassName,
                                "bolt-dialog-callout-content relative scroll-auto",
                                mobile && "bolt-dialog-mobile flex-grow"
                            )}
                            contentJustification={contentJustification}
                            contentLocation={contentLocation}
                            contentOrientation={ContentOrientation.Column}
                            contentRef={this.contentRef}
                            contentShadow={true}
                            contentSize={mobile ? undefined : contentSize}
                            escDismiss={escDismiss}
                            focuszoneProps={{
                                circularNavigation: true,
                                defaultActiveElement: defaultActiveElement || ".bolt-dialog-focus-element",
                                focusOnMount: true,
                                handleTabKey: true,
                                includeDefaults: true,
                                selectInputTextOnFocus: this.props.selectInputTextOnFocus,
                                postprocessKeyStroke: function (event: React.KeyboardEvent<HTMLElement>) {
                                    // We want to prevent moving outside the dialog if there are no focusable elements in the dialog.
                                    event.which === KeyCode.tab && event.preventDefault();
                                    return FocusZoneKeyStroke.IgnoreParents;
                                }
                            }}
                            id={id}
                            lightDismiss={lightDismiss}
                            modal={modal}
                            onDismiss={onDismiss}
                            role={role}
                            height={this.state.height}
                            width={this.state.width}
                            onKeyDown={this.onKeyDown}
                        >
                            <SurfaceContext.Consumer>
                                {surfaceContext => (
                                    <Surface {...surfaceContext} spacing={Spacing.default}>
                                        <div className={css(className, "bolt-dialog-root flex-column flex-grow scroll-hidden")}>
                                            <div
                                                aria-label={defaultFocusableElementAriaLabel}
                                                className="bolt-dialog-focus-element no-outline"
                                                tabIndex={-1}
                                                role={defaultFocusableElementAriaLabel ? "heading" : undefined}
                                                aria-level={defaultFocusableElementAriaLabel ? 1 : undefined}
                                            />
                                            {this.props.children}
                                            {!mobile && resizable && (
                                                <Icon
                                                    ariaLabel={Resources.Resize}
                                                    iconName="GripperResize"
                                                    onMouseDown={this.onGripperMouseDown}
                                                    onKeyDown={this.onGripperKeyDown}
                                                    tabIndex={0}
                                                    wrapperClass="bolt-dialog-resize-icon"
                                                />
                                            )}
                                            {overlay && (
                                                <div className="absolute-fill bolt-dialog-overlay flex-column justify-center">
                                                    <Spinner
                                                        size={SpinnerSize.large}
                                                        ariaLabel={overlay.spinnerAriaLabel}
                                                        ariaLive="assertive"
                                                        label={overlay.spinnerLabel}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </Surface>
                                )}
                            </SurfaceContext.Consumer>
                        </Callout>
                    );
                }}
            </Observer>
        );
    }

    private onGripperKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
        if (this.contentRef.current) {
            const rect = this.contentRef.current.getBoundingClientRect();
            this.currentHeight = rect.height;
            this.currentWidth = rect.width;

            switch (event.keyCode) {
                case KeyCode.leftArrow:
                    this.setState({ width: Math.max(this.currentWidth - DIALOG_RESIZE_INCREMENT, MIN_DIALOG_Size) });
                    break;
                case KeyCode.rightArrow:
                    this.setState({ width: this.currentWidth + DIALOG_RESIZE_INCREMENT });
                    break;
                case KeyCode.upArrow:
                    this.setState({ height: Math.max(this.currentHeight - DIALOG_RESIZE_INCREMENT, MIN_DIALOG_Size) });
                    break;
                case KeyCode.downArrow:
                    this.setState({ height: this.currentHeight + DIALOG_RESIZE_INCREMENT });
                    break;
                default:
                    return;
            }
            event.preventDefault();
            event.stopPropagation();

            announce(format(Resources.SizeFormat, this.state.height || this.currentHeight, this.state.width || this.currentWidth));
        }
    };

    private onGripperMouseDown = (event: React.MouseEvent<HTMLElement>): void => {
        this.onGripperDown(event as any, event.clientX, event.clientY);
        this.attachMouseWindowEvents();
    };

    private onGripperDown = (event: Event, xPos: number, yPos: number): void => {
        if (this.contentRef) {
            event.preventDefault();
            event.stopPropagation();

            this.anchorX = xPos;
            this.anchorY = yPos;

            if (this.contentRef.current) {
                const rect = this.contentRef.current.getBoundingClientRect();
                this.currentHeight = rect.height;
                this.currentWidth = rect.width;
            }
        }
    };

    private onGripperMouseMove = (event: Event): void => {
        this.handleDragEvent(event as any, (event as unknown as React.MouseEvent).clientX, (event as unknown as React.MouseEvent).clientY);
    };

    private handleDragEvent(event: MouseEvent, xPos: number, yPos: number): void {
        const newWidth = Math.max((this.currentWidth || 0) + (xPos - this.anchorX) * 2, MIN_DIALOG_Size);
        const newHeight = Math.max((this.currentHeight || 0) + (yPos - this.anchorY) * 2, MIN_DIALOG_Size);
        this.setState({ width: newWidth, height: newHeight });
    }

    private onGripperMouseUp = (event: Event): void => {
        this.detachMouseWindowEvents();
        announce(format(Resources.SizeFormat, this.state.height, this.state.width));
    };

    private attachMouseWindowEvents(): void {
        this.events.addEventListener(window, "mousemove", this.onGripperMouseMove);
        this.events.addEventListener(window, "mouseup", this.onGripperMouseUp);
    }

    private detachMouseWindowEvents(): void {
        this.events.removeEventListener(window, "mousemove", this.onGripperMouseMove);
        this.events.removeEventListener(window, "mouseup", this.onGripperMouseUp);
    }

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
        const { enterPrimary, primaryButtonProps } = this.props;
        if (enterPrimary && primaryButtonProps && event.which === KeyCode.enter && !event.defaultPrevented) {
            primaryButtonProps.onClick?.(event);
            event.preventDefault();
        }
    };
}
